// src/components/Layout/Header.js

import React, { useContext, useState, useRef, useEffect } from 'react';
import { AuthContext } from '../../store/authContext';
import { 
  Menu, 
  X, 
  Bell, 
  User, 
  Settings, 
  HelpCircle, 
  LogOut,
  ChevronDown 
} from 'lucide-react';
// ✅ Import do React Router pra usar <Link> e navegação
import { Link, useNavigate } from 'react-router-dom';

const Header = ({ pageTitle, onToggleSidebar, sidebarCollapsed }) => {
  const { currentUser, logout } = useContext(AuthContext);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate(); // Para redirecionar após logout, se quiser

  // Obter a data atual formatada
  const getCurrentDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('pt-BR', options);
  };

  // Alternar menu do usuário
  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  // Fechar dropdown ao clicar fora dele
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Obter o rótulo do cargo do usuário
  const getUserRoleLabel = () => {
    const roles = {
      'admin': 'Administrador',
      'manager': 'Gerente',
      'marketing': 'Marketing',
      'basic': 'Básico'
    };
    return roles[currentUser?.role] || 'Usuário';
  };

  // ✅ Handle logout chamando a função do AuthContext
  const handleLogout = async () => {
    const result = await logout();
    // Se quiser redirecionar manualmente:
    if (result.success !== false) {
      navigate('/login');
    }
  };

  return (
    <header className="bg-white h-16 border-b border-gray-200 flex items-center justify-between px-4 lg:px-6 z-30 shadow-sm">
      <div className="flex items-center">
        <button 
          className="btn-icon mr-4 lg:hidden" 
          onClick={onToggleSidebar}
          aria-label={sidebarCollapsed ? "Expandir menu" : "Recolher menu"}
        >
          {sidebarCollapsed ? <Menu size={20} /> : <X size={20} />}
        </button>
        <h1 className="page-title hidden sm:block">{pageTitle}</h1>
      </div>

      <div className="flex items-center space-x-4">
        {/* Data */}
        <span className="text-sm text-gray-500 hidden md:block">
          {getCurrentDate()}
        </span>

        {/* Notificações */}
        <button className="btn-icon relative" aria-label="Notificações">
          <Bell size={20} />
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
            3
          </span>
        </button>

        {/* Menu do Usuário */}
        <div className="relative" ref={dropdownRef}>
          <button 
            className="flex items-center space-x-2 focus:outline-none" 
            onClick={toggleUserMenu}
            aria-expanded={showUserMenu}
            aria-label="Menu do usuário"
          >
            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
              {currentUser?.name?.charAt(0) || 'U'}
            </div>
            <span className="hidden sm:block text-sm font-medium text-gray-700">{currentUser?.name}</span>
            <ChevronDown size={16} className={`text-gray-500 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg py-2 z-40 border border-gray-200">
              {/* Info do usuário */}
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="font-medium text-gray-800">{currentUser?.name}</div>
                <div className="text-sm text-gray-500">{getUserRoleLabel()}</div>
                <div className="text-sm text-gray-500 truncate">{currentUser?.email}</div>
              </div>

              {/* Links */}
              <div className="py-2">
                <Link 
                  to="/profile"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setShowUserMenu(false)}
                >
                  <User size={16} className="mr-3 text-gray-500" />
                  <span>Meu Perfil</span>
                </Link>

                <Link 
                  to="/settings"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setShowUserMenu(false)}
                >
                  <Settings size={16} className="mr-3 text-gray-500" />
                  <span>Configurações</span>
                </Link>

                <Link 
                  to="/help"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setShowUserMenu(false)}
                >
                  <HelpCircle size={16} className="mr-3 text-gray-500" />
                  <span>Ajuda</span>
                </Link>
              </div>

              {/* Logout */}
              <div className="border-t border-gray-200 mt-2 pt-2">
                <button
                  onClick={handleLogout}
                  className="w-full text-left flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                >
                  <LogOut size={16} className="mr-3" />
                  <span>Sair</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
